import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { LogOut, User, ChevronDown, HelpCircle, Users, Key, Menu, X } from 'lucide-react';
import { useNavigationItems } from './Navigation';
import { useNavigate, Link, useLocation } from 'react-router-dom';

export default function Header() {
  const { user, signOut, isDataManager, isSlaughterhouseUser } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationItems = useNavigationItems();

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getUserDisplayName = () => {
    if (user?.displayName) return user.displayName;
    return user?.email?.split('@')[0] || 'Usuario';
  };

  const getUserRole = () => {
    if (isDataManager) return 'Gestor de Datos';
    if (isSlaughterhouseUser) return 'Usuario Matadero';
    return 'Usuario';
  };

  const handleNavigateToUsers = () => {
    navigate('/users');
    setIsDropdownOpen(false);
  };

  const handleShowHelp = () => {
    setIsHelpModalOpen(true);
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsDropdownOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="header-modern">
      <div className="w-full px-4" style={{ maxWidth: 'none' }}>
        <div className="flex items-center w-full gap-6">

          {/* Logo and Title Section */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="logo-container">
              <div className="logo-icon">🐄</div>
            </div>
            <div className="header-text">
              <h1 className="header-title">SISTEMA</h1>
              <p className="header-subtitle">Trazabilidad Manual</p>
            </div>
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="mobile-menu-button"
              aria-label="Toggle navigation menu"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="flex-1 flex justify-center">
            <nav className="nav-pills">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-pill ${isActive ? 'active' : ''}`}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Desktop User Actions Section */}
          {user && (
            <div className="relative flex-shrink-0 desktop-user-dropdown" ref={dropdownRef}>
              {/* User Profile Button */}
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="user-profile-button"
              >
                <div className="user-avatar-modern">
                  <User size={16} className="text-white" />
                </div>
                <div className="user-info-modern">
                  <div className="user-name-modern">{getUserDisplayName()}</div>
                  <div className="user-role-modern">{getUserRole()}</div>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-white/80 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''
                    }`}
                />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="user-dropdown">
                  {/* User Info Header */}
                  <div className="user-dropdown-header">
                    <div className="user-avatar-large">
                      <User size={20} className="text-green-600" />
                    </div>
                    <div className="user-dropdown-info">
                      <div className="user-dropdown-name">{getUserDisplayName()}</div>
                      <div className="user-dropdown-email">{user.email}</div>
                      <div className="user-dropdown-role">{getUserRole()}</div>
                    </div>
                  </div>

                  <div className="user-dropdown-divider"></div>

                  {/* Menu Items */}
                  <div className="user-dropdown-menu">
                    {isDataManager && (
                      <>
                        <button
                          onClick={handleNavigateToUsers}
                          className="user-dropdown-item"
                        >
                          <Users size={16} />
                          <span>Gestión de Usuarios</span>
                        </button>
                        <button
                          onClick={() => navigate('/management/api-keys')}
                          className="user-dropdown-item"
                        >
                          <Key size={16} />
                          <span>Gestión de API Keys</span>
                        </button>
                      </>
                    )}
                    <button
                      onClick={handleShowHelp}
                      className="user-dropdown-item"
                    >
                      <HelpCircle size={16} />
                      <span>Acerca de</span>
                    </button>
                  </div>

                  <div className="user-dropdown-divider"></div>

                  {/* Sign Out */}
                  <button
                    onClick={handleSignOut}
                    className="user-dropdown-item user-dropdown-signout"
                  >
                    <LogOut size={16} />
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-sidebar-overlay" onClick={closeMobileMenu}>
          <div className="mobile-sidebar-menu" onClick={(e) => e.stopPropagation()}>
            {/* User Profile Section */}
            {user && (
              <div className="mobile-user-section">
                <div className="mobile-user-info">
                  <div className="mobile-user-avatar">
                    <User size={24} className="text-green-600" />
                  </div>
                  <div className="mobile-user-details">
                    <div className="mobile-user-name">{getUserDisplayName()}</div>
                    <div className="mobile-user-email">{user.email}</div>
                    <div className="mobile-user-role">{getUserRole()}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Items */}
            <div className="mobile-nav-section">
              <h3 className="mobile-section-title">Navegación</h3>
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`mobile-nav-item ${isActive ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* User Actions */}
            {user && (
              <div className="mobile-actions-section">
                <h3 className="mobile-section-title">Opciones</h3>
                {isDataManager && (
                  <>
                    <button
                      onClick={() => {
                        navigate('/users');
                        closeMobileMenu();
                      }}
                      className="mobile-action-item"
                    >
                      <Users size={20} />
                      <span>Gestión de Usuarios</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/management/api-keys');
                        closeMobileMenu();
                      }}
                      className="mobile-action-item"
                    >
                      <Key size={20} />
                      <span>Gestión de API Keys</span>
                    </button>
                  </>
                )}
                <button
                  onClick={handleShowHelp}
                  className="mobile-action-item"
                >
                  <HelpCircle size={20} />
                  <span>Acerca de</span>
                </button>
                <button
                  onClick={() => {
                    handleSignOut();
                    closeMobileMenu();
                  }}
                  className="mobile-action-item mobile-signout"
                >
                  <LogOut size={20} />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Ayuda/Acerca de */}
      {isHelpModalOpen && (
        <div className="help-modal-overlay">
          <div className="help-modal">
            {/* Header del Modal */}
            <div className="help-modal-header">
              <h3 className="help-modal-title">Acerca del Sistema</h3>
              <button
                onClick={() => setIsHelpModalOpen(false)}
                className="help-modal-close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido del Modal */}
            <div className="help-modal-content">
              <div className="help-modal-logo-section">
                <div className="help-modal-logo">🐄</div>
                <h4 className="help-modal-app-name">Natura Ganado</h4>
                <p className="help-modal-app-description">Sistema de Trazabilidad Manual</p>
              </div>

              <div className="help-modal-info">
                <div className="help-modal-info-row">
                  <span className="help-modal-info-label">Versión:</span>
                  <span className="help-modal-info-value">v1.0.0</span>
                </div>
                <div className="help-modal-info-row">
                  <span className="help-modal-info-label">Build:</span>
                  <span className="help-modal-info-value">2025.06.20</span>
                </div>
                <div className="help-modal-info-row">
                  <span className="help-modal-info-label">Entorno:</span>
                  <span className="help-modal-info-value">{process.env.NODE_ENV === 'production' ? 'Producción' : 'Desarrollo'}</span>
                </div>
                <div className="help-modal-info-row">
                  <span className="help-modal-info-label">Usuario:</span>
                  <span className="help-modal-info-value">{getUserRole()}</span>
                </div>
              </div>

              <div className="help-modal-footer-info">
                <p className="help-modal-copyright">
                  © 2025 Natura Ganado. Todos los derechos reservados.
                </p>
                <p className="help-modal-description">
                  Desarrollado para la gestión profesional de trazabilidad ganadera
                </p>
              </div>
            </div>

            {/* Footer del Modal */}
            <div className="help-modal-button-section">
              <button
                onClick={() => setIsHelpModalOpen(false)}
                className="help-modal-close-button"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}